import React, { useState, useEffect, useMemo } from "react";
import {
  Form,
  Button,
  message,
  Descriptions,
  Card,
  Divider,
  Space,
  Alert,
  Tabs,
  Typography,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { binaryContentsHooks as cHooks } from "../hooks";

const { Text, Paragraph } = Typography;

const UserEditPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // ✅ 설문 관련 상태
  const [servayTabs, setServayTabs] = useState([]); // [{id,type}]
  const [activeServayId, setActiveServayId] = useState(null);
  const [servayResult, setServayResult] = useState(null); // result.data[0] 같은 형태
  const [servayLoading, setServayLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const userData = useMemo(
    () => location.state?.userData ?? location.state?.contentData ?? null,
    [location.state]
  );
  const pageFromList = location.state?.page;

  // ---------------------------
  // ✅ 설문 목록 가져오기 (탭)
  // ---------------------------
  const fetchServayList = async () => {
    if (!userData?.id) return;
    setServayLoading(true);
    try {
      // ⚠️ 여기 id 키는 너희 API에 맞춰야 함
      const res = await cHooks.servayList({ id: userData.id });

      if (res?.IsError) throw new Error("servayList failed");
      if (!res?.data?.status) throw new Error("servayList status false");

      const list = res.data.data || [];
      setServayTabs(list);

      // 첫 탭 자동 선택
      if (list.length > 0) {
        setActiveServayId(list[0].id);
      }
    } catch (e) {
      message.error("설문 목록 불러오기 실패!");
    } finally {
      setServayLoading(false);
    }
  };

  // ---------------------------
  // ✅ 설문 결과 가져오기
  // ---------------------------
  const fetchServayResult = async (servayId) => {
    if (!userData?.id || !servayId) return;
    setServayLoading(true);
    try {
      // ⚠️ 파라미터 키는 너희 API에 맞춰야 함
      const res = await cHooks.servayResultList({
        user_id: userData.id,
        servay_id: servayId,
      });

      if (res?.IsError) throw new Error("servayResult failed");
      if (!res?.data?.status) throw new Error("servayResult status false");

      const first = (res.data.data || [])[0] || null; // 보통 최신 1개만
      setServayResult(first);
    } catch (e) {
      setServayResult(null);
      message.error("설문 결과 불러오기 실패!");
    } finally {
      setServayLoading(false);
    }
  };

  // 유저 들어오면 설문 탭 목록 호출
  useEffect(() => {
    if (!userData) return;

    form.setFieldsValue({
      // (너가 쓰던 유저 setFieldsValue는 필요하면 유지)
    });

    fetchServayList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // 탭 바뀌면 결과 호출
  useEffect(() => {
    if (!activeServayId) return;
    fetchServayResult(activeServayId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServayId]);

  if (!userData) {
    return (
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: 20 }}>
        <Card title="유저 수정 페이지">
          <Alert
            type="warning"
            showIcon
            message="전달된 userData가 없습니다."
            description='목록에서 이동할 때 navigate(..., { state: { userData } }) 로 넘겨주세요.'
          />
          <Divider />
          <Button type="primary" onClick={() => navigate("/users")}>
            목록으로
          </Button>
        </Card>
      </div>
    );
  }

  // ---------------------------
  // ✅ response가 어떤 옵션인지 계산 (0/1 기반 모두 대응)
  // ---------------------------
  const getSelectedIndex = (q) => {
    const answers = Array.isArray(q?.answer) ? q.answer : [];
    const r = q?.response;

    if (!answers.length) return null;

    // 숫자면 index로 해석 (0-based 또는 1-based)
    if (typeof r === "number") {
      if (r >= 0 && r < answers.length) return r; // 0-based
      if (r >= 1 && r <= answers.length) return r - 1; // 1-based
      return null;
    }

    // 문자열이면 answer 값과 매칭 시도
    if (typeof r === "string") {
      const idx = answers.findIndex((a) => String(a).trim() === r.trim());
      return idx >= 0 ? idx : null;
    }

    return null;
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: 20 }}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {/* ✅ 유저 정보 */}
        <Card title="유저 상세 정보">
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="이름">{userData.user_name}</Descriptions.Item>
            <Descriptions.Item label="성별">
              {userData.gender === "0" || userData.gender === 0
                ? "여"
                : userData.gender === "1" || userData.gender === 1
                ? "남"
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item label="생년월일">{userData.birth}</Descriptions.Item>
            <Descriptions.Item label="전화번호">{userData.hp}</Descriptions.Item>

            <Descriptions.Item label="가입일">{userData.created_at}</Descriptions.Item>
            <Descriptions.Item label="로그인 플랫폼">{userData.login_type}</Descriptions.Item>
          </Descriptions>

          {/* ✅ 유저 셀 밑에 설문 요약 텍스트 */}
          <Divider />
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            설문 결과 요약
          </Text>

          {servayResult ? (
            <div style={{ lineHeight: 1.6 }}>
              <div>
                <Text strong>Total Score:</Text> <Text>{servayResult.total_score}</Text>
              </div>
              <div>
                <Text strong>Grade:</Text> <Text>{servayResult.score_grade}</Text>
              </div>
              <div>
                <Text strong>Created At:</Text> <Text>{servayResult.created_at}</Text>
              </div>

              <Divider style={{ margin: "12px 0" }} />

              <Paragraph style={{ whiteSpace: "pre-line", marginBottom: 8 }}>
                {servayResult.guide_text}
              </Paragraph>
              <Paragraph style={{ whiteSpace: "pre-line", marginBottom: 0 }}>
                {servayResult.report_text}
              </Paragraph>
            </div>
          ) : (
            <Text type="secondary">
              {servayLoading ? "설문 결과 불러오는 중..." : "설문 결과가 없습니다."}
            </Text>
          )}
        </Card>

        {/* ✅ 설문 탭 + 설문 폼 */}
        <Card title="설문">
          <Tabs
            loading={servayLoading}
            activeKey={activeServayId || undefined}
            onChange={(key) => setActiveServayId(key)}
            items={(servayTabs || []).map((t) => ({
              key: t.id,
              label: t.type, // 탭 이름
            }))}
          />

          {!servayResult?.response?.length ? (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="선택한 설문 결과가 없습니다."
            />
          ) : (
            <div style={{ marginTop: 12 }}>
              {servayResult.response.map((q, idx) => {
                const isChoice = q?.type === 2 && Array.isArray(q?.answer);
                const selectedIdx = isChoice ? getSelectedIndex(q) : null;

                return (
                  <div
                    key={`${q.key}-${idx}`}
                    style={{
                      padding: 14,
                      border: "1px solid #eee",
                      borderRadius: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>
                      {idx + 1}. {q.question}
                    </div>

                    {/* ✅ 타입 1: 숫자/텍스트 응답 */}
                    {q?.type === 1 && (
                      <div
                        style={{
                          display: "inline-block",
                          padding: "10px 14px",
                          borderRadius: 10,
                          background: "#091527",
                          color: "#fff",
                          fontWeight: 600,
                        }}
                      >
                        {q.response}
                        {typeof q.answer === "string" ? ` ${q.answer}` : ""}
                      </div>
                    )}

                    {/* ✅ 타입 2: 선택지 버튼 (사진 같은 UI) */}
                    {isChoice && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 10,
                        }}
                      >
                        {q.answer.map((opt, optIdx) => {
                          const selected = selectedIdx === optIdx;

                          return (
                            <div
                              key={`${q.key}-opt-${optIdx}`}
                              style={{
                                padding: "14px 12px",
                                borderRadius: 12,
                                textAlign: "center",
                                fontWeight: 700,
                                cursor: "default",
                                background: selected ? "#091527" : "#f3f4f6",
                                color: selected ? "#fff" : "#6b7280",
                                border: selected ? "1px solid #091527" : "1px solid #e5e7eb",
                                userSelect: "none",
                              }}
                              title={`response=${q.response}`}
                            >
                              {opt}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ✅ 디버그/검증용: response 값도 같이 보이게(원하면 제거) */}
                    <div style={{ marginTop: 10, color: "#9ca3af", fontSize: 12 }}>
                      {/* key: {q.key} / response: {String(q.response)} */}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Space>
          <Button onClick={() => navigate(pageFromList ? `/users?page=${pageFromList}` : "/users")}>
            목록으로
          </Button>
        </Space>
      </Space>
    </div>
  );
};

export default UserEditPage;
